/* eslint-disable react/no-danger */
import React from 'react'

import { convertToSlug } from 'src/utils/IdGenerator'
import Button from '@components/Button/button'
import ComparisonTable from '@components/ComparisonTable/comparisonTable'
import ErrorBoundary from '@components/ErrorBoundary/errorBoundary'
import { TWO_COLIUMN_COMPARE_TABLE } from './constants'
import styles from './table.module.scss'

type TableSectionData = {
  tableContent: string | null
  sectionHeading: string | null
  tableFooterSection: string | null
  tableVariant: string | null
  buttonCta: any
}

const cheerio = typeof window === 'undefined' ? global.cheerio : window.cheerio

const getModifiedHtml = tableContent => {
  // Load the HTML content into a Cheerio instance
  const $ = cheerio.load(tableContent || '', null, false)

  // Remove all style attributes from elements
  $('*').removeAttr('style')

  // convert all the th tags to td;
  $('th').each((_, element) => {
    const td = $('<td>').html($(element).html())
    $(element).replaceWith(td)
  })

  // replace content inside td with just td
  $('thead td').each((_, element) => {
    $(element).html($(element).text())
  })

  // replace content inside td with just td for body

  $('tbody td').each((_, element) => {
    // Get the inner HTML of the td element
    const hasATag = $(element).find('a').length > 0
    if (hasATag) {
      // Remove <strong> and <p> tags while keeping the <a> tag
      $(element).find('strong').contents().unwrap() // Remove <strong> tags
      $(element).find('p').contents().unwrap() // Remove <p> tags
    } else {
      $(element).html($(element).text())
    }
  })

  // Get the number of columns in the table
  const columnCount = $('thead tr td').length

  // Create a new tr with empty td tags according to the number of columns
  const newTr = $('<tr></tr>')
  for (let i = 0; i < columnCount; i++) {
    newTr.append('<td>&nbsp;</td>')
  }

  // Insert the new tr after the tbody tag
  $('tbody').prepend(newTr)

  const $tds = $('table td')

  // Add class 'empty' to td elements with empty content
  $tds.each((_, td) => {
    const $td = $(td)
    const content = $td.text().trim()
    const hasImages = $td.find('img').length > 0
    if (content === '' && !hasImages) {
      $td.css({
        visibility: 'hidden',
      })
    }
  })

  // Find the first row (tr) element in the table
  const $firstRow = $('thead tr').first()

  // Get the content of each cell in the first row
  const cellContents = $firstRow
    .find('td')
    .map((_, cell) => $(cell).text().trim())
    .get()

  // Find all tr elements in the table except the first one
  const $dataRows = $('tbody tr').slice(1)

  // Add a data-heading attribute with the value from the first row to each td in the subsequent rows
  $dataRows.each((_, row) => {
    const $tdss = $(row).find('td').slice(1) // Exclude the first td
    $tdss.each((index, td) => {
      $(td).attr('data-heading', cellContents[index + 1]) // Offset by 1 to skip the first td
    })
  })

  $('img').attr('loading', 'lazy')
  $('img').attr('decoding', 'async')

  $('a').each((_index, element) => {
    const $a = $(element)
    $a.attr('target', '_blank')
  })

  // Get the modified HTML content
  const modifiedHTML = $.html()
  return modifiedHTML
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function generateTableSchema(tableContent, headingHtml, currentUrl) {
  const $ = cheerio.load(tableContent)

  const $table = $('table').first()

  // Extract plan names from the first row (thead)
  const planNames = $table
    .find('thead tr')
    .first()
    .find('td')
    .slice(1)
    .map((_, td) => $(td).text().trim())
    .get()

  // Extract each row from tbody
  const variableMeasured: any = []
  const propertyNames: string[] = []

  $table
    .find('tbody tr')
    .slice(1)
    .each((_, row) => {
      // skip empty row inserted
      const $tds = $(row).find('td')
      const propertyName = $tds.first().text().trim() // first td = property name
      propertyNames.push(propertyName)

      const values = $tds
        .slice(1)
        .map((i, td) => ({
          '@type': 'PropertyValue',
          name: planNames[i],
          value: $(td).text().trim(),
        }))
        .get()

      variableMeasured.push({
        '@type': 'PropertyValue',
        name: propertyName,
        value: values,
      })
    })

  const $h = cheerio.load(headingHtml || '')
  const heading = $h('h1, h2, h3, h4').first().text().trim()
  const description = $h('p').first().text().trim()

  // Build final Dataset schema
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: heading || 'Table',
    description: description || propertyNames.join(', '),
    url: currentUrl,
    creator: { '@type': 'Organization', name: 'Acko Insurance' },
    license: 'https://www.acko.com/terms-and-conditions/',
    variableMeasured,
    distribution: {
      '@type': 'DataDownload',
      encodingFormat: 'text/html',
      contentUrl: currentUrl,
    },
  }

  return JSON.stringify(schema, null, 2)
}

const createTableSectionData = (data: any): TableSectionData => {
  const attributes = data?.data?.attributes

  const rawTableContent = attributes?.tableContent || null
  const cleanedTableContent = rawTableContent?.replace(/<colgroup>.*?<\/colgroup>/s, '') || null

  const buttonCta = attributes?.tableSectionCTA?.[0] || null

  return {
    tableContent: cleanedTableContent,
    sectionHeading: attributes?.tableSectionHeading || null,
    tableFooterSection: attributes?.tableFooterSection || null,
    tableVariant: attributes?.tableVariant || null,
    buttonCta,
  }
}

const Table = ({ data }) => {
  const { tableContent, sectionHeading, tableFooterSection, tableVariant, buttonCta } = createTableSectionData(data)
  const modifiedHtml = getModifiedHtml(tableContent)
  const buttonName = buttonCta?.name

  // Extract the h2 content and headerTabName
  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/
  const matches = sectionHeading?.match(h2Regex)
  const h2Content = matches ? matches[1] : null
  const idContent = data?.data?.attributes?.headerTabName || h2Content

  // temporarily removed till table schema is fixed
  // const tableSchema = generateTableSchema(modifiedHtml, sectionHeading, data.currentUrl)

  if (tableVariant === TWO_COLIUMN_COMPARE_TABLE) {
    return <ComparisonTable data={data} />
  }

  return (
    <ErrorBoundary data={data}>
      <div className={`${styles[data?.data?.attributes?.tableVariant]} white`} id={convertToSlug(idContent)}>
        <div dangerouslySetInnerHTML={{ __html: getModifiedHtml(sectionHeading) }} />
        <div
          dangerouslySetInnerHTML={{ __html: modifiedHtml }}
          className={
            data?.data?.attributes?.centerAlignTableHeaderInEachColumn === 'true'
              ? `${styles.tableStyles} ${styles.centerTableStyles}`
              : styles.tableStyles
          }
        />
        {tableFooterSection && (
          <div dangerouslySetInnerHTML={{ __html: tableFooterSection }} className={styles.footerSection} />
        )}
        {buttonName && (
          <>
            {buttonCta?.sectionCTAPara && <span dangerouslySetInnerHTML={{ __html: buttonCta?.sectionCTAPara }} />}

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '3rem' }}>
              <Button
                data={{ ...buttonCta, alignCenter: true }}
                segmentEventCommonProperties={data?.segmentEventCommonProperties}
              />
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  )
}

export default Table
